import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { reactive, ref, PropType, computed } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType, TABLE_PREVIEW_PAGE_SIZE, TABLE_FULL_PAGE_SIZE_DEFAULT } from '@/views/dashboard/_enum/table-view-mode'
import { WhiteListViewService, WhiteListItem, WhiteListQueryParams } from './whiteList-view/whiteList-view.service'
import { PopupViewService } from './popup-view/popup-view.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { FormType } from '@/components/cmForm/types'
import { ElMessage, ElMessageBox } from 'element-plus'

const DefaultPopType = 4

export default /*#__PURE__*/_defineComponent({
  __name: 'trust-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  hasPermission: {
    type: Boolean,
    default: false
  }
},
  emits: ["selectionChange", "untrustSuccess", "viewAll"],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t } = useI18n()
const popupTypeDict = computed(() => ['', t('popupIntercept.filterBottomRightBubble'), t('popupIntercept.filterCenterBubble'), t('popupIntercept.filterBundledSoft')])
const popupTypes = computed(() => [
  { label: t('popupIntercept.filterAll'), value: 4 },
  { label: t('popupIntercept.filterBottomRightBubble'), value: 1 },
  { label: t('popupIntercept.filterCenterBubble'), value: 2 },
  { label: t('popupIntercept.filterBundledSoft'), value: 3 }
])

const props = __props

const emits = __emit

const injector = useReflectiveInjector([WhiteListViewService, PopupViewService])
const whiteListViewService = injector.get(WhiteListViewService)
const popupViewService = injector.get(PopupViewService)
const dataApi = whiteListViewService.fetchWhiteList.bind(whiteListViewService)

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const selectedIds = ref<string[]>([])
const selectedRows = ref<WhiteListItem[]>([])

const queryParams = reactive<WhiteListQueryParams>({
  key_word: '',
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? TABLE_PREVIEW_PAGE_SIZE : TABLE_FULL_PAGE_SIZE_DEFAULT,
  popup_type: DefaultPopType
})

const tableOpt = computed<TableOpt>(() => ({
  bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : undefined,
  columns: [
    { prop: 'soft_name', label: t('popupIntercept.softName'), columnOpt: { minWidth: 150 } },
    {
      prop: 'popup_type',
      label: t('popupIntercept.popupType'),
      formatter: (_row, val) => popupTypeDict.value[val] || '--',
      columnOpt: { minWidth: 120 }
    }
  ],
  rowKey: 'soft_id',
  selection: true,
  path: 'list',
  height: props.viewMode === TableViewMode.preview ? undefined : '100%',
  hiddenPageTool: props.viewMode === TableViewMode.preview,
  disableScrollLoad: props.viewMode === TableViewMode.preview
}))

const tableActOpt = computed<TableActOpt>(() => ({
  isLink: false,
  height: '100%',
  columnOpt: { label: t('popupIntercept.operation'), width: 120, align: 'left' },
  actionButtons: () => []
}))

const filterFormColumns = computed(() => [
  {
    prop: 'popup_type',
    label: t('popupIntercept.popupType'),
    colOpt: { style: 'flex: 0 0 150px' },
    hiddenAllLabel: true,
    outSearchFiledFlag: true,
    selectOpt: { clearable: true },
    type: FormType.Select,
    dictData: popupTypes.value,
    events: {
      change: handleQuery
    }
  }
])

function handleSelectionChange(selection: WhiteListItem[], ids: string[]) {
  selectedRows.value = selection ?? []
  selectedIds.value = ids ?? []
  emits('selectionChange', ids)
}

function handleSearch() {
  queryParams.page = 1
  void refreshList()
}

async function handleQuery() {
  queryParams.page = 1
  await refreshList()
}

function handleReset() {
  queryParams.key_word = ''
  queryParams.popup_type = DefaultPopType
  queryParams.page = 1
  void refreshList()
}

async function handleUntrust() {
  if (!selectedRows.value.length) return
  try {
    await ElMessageBox.confirm(t('popupIntercept.confirmUntrust'), t('cmtable.tip'), {
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel')
    })
  } catch {
    return
  }
  const popup_list = selectedRows.value.map((item) => ({
    soft_id: item.soft_id,
    popup_type: item.popup_type
  }))
  try {
    const res = await popupViewService.trustOrder({ popup_list, is_trust: false })
    tableRef.value?.tableRef?.clearSelection?.()
    selectedRows.value = []
    selectedIds.value = []
    ElMessage.success(t('popupIntercept.untrustSuccess', {
      terminalCount: res.terminal_count || 0,
      popupCount: popup_list.length
    }))
    emits('untrustSuccess')
    void refreshList()
  } catch {
    ElMessage.error(t('popupIntercept.untrustFailed', { popupCount: popup_list.length }))
  }
}

__expose({
  tableRef,
  selectedIds
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createBlock(CmTable, {
    ref_key: "tableRef",
    ref: tableRef,
    tableOpt: tableOpt.value,
    tableActOpt: tableActOpt.value,
    firstLoading: true,
    "has-permission": __props.hasPermission,
    api: _unref(dataApi),
    modelValue: queryParams,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
    pageOption: {
      style: 'padding-bottom: 16px',
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
    onChangeSelected: handleSelectionChange
  }, {
    header: _withCtx(() => [
      _createVNode(SearchFilterBar, {
        modelValue: queryParams,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
        "form-columns": filterFormColumns.value,
        disabled: !__props.hasPermission,
        "show-search": true,
        "search-placeholder": _ctx.$t('popupIntercept.searchPlaceholderSoft'),
        "popover-threshold": 2,
        onSearch: handleSearch,
        onQuery: handleQuery,
        onReset: handleReset
      }, {
        actions: _withCtx(() => [
          _createVNode(_component_el_button, {
            size: "small",
            type: "primary",
            disabled: !selectedRows.value.length || !__props.hasPermission,
            onClick: handleUntrust
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('popupIntercept.untrust')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          (__props.viewMode === _unref(TableViewMode).preview)
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                size: "small",
                type: "",
                plain: "",
                disabled: !__props.hasPermission,
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emits('viewAll')))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('commonPage.viewAll')), 1)
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"])
    ]),
    _: 1
  }, 8, ["tableOpt", "tableActOpt", "has-permission", "api", "modelValue"]))
}
}

})